class DBRMANGalleryRoom {
  constructor(section) {
    this.section = section;
    this.room = section.querySelector('[data-room]');
    this.guide = section.querySelector('[data-guide]');

    this.surfaces = {
      top: section.querySelector('[data-surface="top"]'),
      bottom: section.querySelector('[data-surface="bottom"]'),
      left: section.querySelector('[data-surface="left"]'),
      middle: section.querySelector('[data-surface="middle"]'),
      right: section.querySelector('[data-surface="right"]')
    };

    this.planes = {
      left: section.querySelector('[data-plane="left"]'),
      back: section.querySelector('[data-plane="back"]'),
      right: section.querySelector('[data-plane="right"]')
    };

    this.tracks = {
      left: section.querySelector('[data-track="left"]'),
      back: section.querySelector('[data-track="back"]'),
      right: section.querySelector('[data-track="right"]')
    };

    this.linesSvg = section.querySelector('[data-lines]');
    this.backRect = section.querySelector('[data-line-rect]');

    if (!this.room || !this.guide || !this.planes.left || !this.planes.back || !this.planes.right || !this.tracks.left || !this.tracks.back || !this.tracks.right) return;

    this.autoplay = section.dataset.autoplay === 'true';
    this.pauseOnHover = section.dataset.pauseOnHover === 'true';
    this.autoplaySpeed = Math.max(1, Math.min(100, Number(section.dataset.autoplaySpeed || 18)));
    this.pauseStrength = Math.max(0, Math.min(100, Number(section.dataset.pauseStrength || 100))) / 100;
    this.mediaHeightDesktop = Math.max(10, Math.min(100, Number(section.dataset.mediaHeightDesktop || 45)));
    this.mediaHeightMobile = Math.max(10, Math.min(100, Number(section.dataset.mediaHeightMobile || 40)));
    this.wallWidthDesktop = Math.max(25, Math.min(100, Number(section.dataset.wallWidthDesktop || 60)));
    this.wallWidthMobile = Math.max(25, Math.min(100, Number(section.dataset.wallWidthMobile || 60)));
    this.wallHeightDesktop = Math.max(25, Math.min(100, Number(section.dataset.wallHeightDesktop || 65)));
    this.wallHeightMobile = Math.max(25, Math.min(100, Number(section.dataset.wallHeightMobile || 65)));
    this.wallAngleDesktop = Math.max(0, Math.min(180, Number(section.dataset.wallAngleDesktop || 68)));
    this.wallAngleMobile = Math.max(0, Math.min(180, Number(section.dataset.wallAngleMobile || 68)));
    this.roomDepthDesktop = Math.max(0, Math.min(100, Number(section.dataset.roomDepthDesktop || 0)));
    this.roomDepthMobile = Math.max(0, Math.min(100, Number(section.dataset.roomDepthMobile || 0)));

    this.lockedMobileHeight = window.innerHeight;
    this.geometry = null;
    this.setWidth = 1;
    this.scroll = 0;
    this.velocity = 0;
    this.dragging = false;
    this.moved = false;
    this.pointerOverMedia = false;
    this.lastX = 0;
    this.lastTime = 0;
    this.lastFrame = 0;
    this.raf = null;
    this.rebuildTimer = null;
    this.suppressClickUntil = 0;
    this.pointerDownLink = null;
    this.pointerDownX = 0;
    this.pointerDownY = 0;

    this.pointerDown = this.pointerDown.bind(this);
    this.pointerMove = this.pointerMove.bind(this);
    this.pointerUp = this.pointerUp.bind(this);
    this.scheduleRebuild = this.scheduleRebuild.bind(this);
    this.tick = this.tick.bind(this);

    this.bind();
    this.applyLockedMobileHeight();
    this.updateLayout();
    this.render();
    this.syncThemeColor();
    this.raf = requestAnimationFrame(this.tick);
  }

  isMobile() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  currentWallWidth() { return this.isMobile() ? this.wallWidthMobile : this.wallWidthDesktop; }
  currentWallHeight() { return this.isMobile() ? this.wallHeightMobile : this.wallHeightDesktop; }
  currentWallAngleRaw() { return this.isMobile() ? this.wallAngleMobile : this.wallAngleDesktop; }
  currentRoomDepth() { return this.isMobile() ? this.roomDepthMobile : this.roomDepthDesktop; }

  applyLockedMobileHeight() {
    if (!this.isMobile()) {
      this.section.style.removeProperty('--dgr-locked-mobile-height');
      return;
    }

    this.section.style.setProperty('--dgr-locked-mobile-height', `${this.lockedMobileHeight}px`);
  }

  currentWallAngleMeta() {
    const raw = this.currentWallAngleRaw();
    let rotateAngle = raw <= 90 ? raw : 180 - raw;
    rotateAngle = Math.max(0, Math.min(89, rotateAngle));

    return {
      raw,
      rotateAngle,
      anglePower: Math.sin((rotateAngle * Math.PI) / 180)
    };
  }

  cssValue(value) {
    return typeof value === 'number' ? `${value}px` : value;
  }

  positionBox(el, left, top, width, height, transform = '') {
    if (!el) return;
    el.style.left = this.cssValue(left);
    el.style.top = this.cssValue(top);
    el.style.width = this.cssValue(width);
    el.style.height = this.cssValue(height);
    el.style.transform = transform;
  }

  setPath(name, d) {
    const path = this.section.querySelector(`[data-line="${name}"]`);
    if (path) path.setAttribute('d', d);
  }

  setGradient(name, x1, y1, x2, y2) {
    const gradient = this.section.querySelector(`[data-gradient="${name}"]`);
    const path = this.section.querySelector(`[data-line="${name}"]`);
    if (!gradient || !path) return;

    gradient.setAttribute('x1', x1);
    gradient.setAttribute('y1', y1);
    gradient.setAttribute('x2', x2);
    gradient.setAttribute('y2', y2);
    path.setAttribute('stroke', `url(#${gradient.id})`);
  }

  findLinkAtPoint(x, y) {
    const elements = document.elementsFromPoint(x, y);

    for (const el of elements) {
      if (!this.section.contains(el)) continue;
      const link = el.closest && el.closest('.dgr-link');
      if (link && this.section.contains(link)) return link;
    }

    return null;
  }

  getGeometry() {
    const roomRect = this.room.getBoundingClientRect();
    const baseWallWidth = roomRect.width * (this.currentWallWidth() / 100);
    const baseWallHeight = roomRect.height * (this.currentWallHeight() / 100);
    const angleMeta = this.currentWallAngleMeta();
    const depthRatio = this.currentRoomDepth() / 100;

    const distanceScale = Math.max(0.28, 1 - depthRatio * 0.35 - angleMeta.anglePower * 0.18);
    const wallWidth = Math.max(100, Math.min(roomRect.width, baseWallWidth * distanceScale));
    const wallHeight = Math.max(100, Math.min(roomRect.height, baseWallHeight * distanceScale));
    const wallLeft = (roomRect.width - wallWidth) / 2;
    const wallRight = wallLeft + wallWidth;
    const wallTop = (roomRect.height - wallHeight) / 2;
    const wallBottom = wallTop + wallHeight;

    this.guide.style.width = `${(wallWidth / roomRect.width) * 100}%`;
    this.guide.style.height = `${(wallHeight / roomRect.height) * 100}%`;

    const seamOverlap = 0;
    const angleRad = Math.max(0.001, angleMeta.rotateAngle) * Math.PI / 180;
    const safeCos = Math.max(0.08, Math.cos(angleRad));
    const wallDepthBoost = 1 + depthRatio * 1.7 + angleMeta.anglePower * 1.2;
    const leftPlaneWidth = Math.max(180, (wallLeft / safeCos) * wallDepthBoost + 100 + seamOverlap);
    const rightPlaneWidth = Math.max(180, ((roomRect.width - wallRight) / safeCos) * wallDepthBoost + 100 + seamOverlap);

    return {
      roomW: roomRect.width,
      roomH: roomRect.height,
      wallLeft,
      wallRight,
      wallTop,
      wallBottom,
      wallWidth,
      wallHeight,
      leftPlaneWidth,
      rightPlaneWidth,
      rotateAngle: angleMeta.rotateAngle,
      rawAngle: angleMeta.raw,
      anglePower: angleMeta.anglePower,
      depthRatio,
      seamOverlap,
      backStart: leftPlaneWidth,
      rightStart: leftPlaneWidth + wallWidth
    };
  }

  getMediaHeight(g) {
    const percent = this.isMobile() ? this.mediaHeightMobile : this.mediaHeightDesktop;
    const baseHeight = g.roomH;
    const maxSafeHeight = g.wallHeight * 0.92;
    return Math.max(20, Math.round(Math.min(baseHeight * (percent / 100), maxSafeHeight)));
  }

  updateLines(g) {
    if (!this.linesSvg) return;

    this.linesSvg.setAttribute('viewBox', `0 0 ${g.roomW} ${g.roomH}`);
    this.linesSvg.setAttribute('width', g.roomW);
    this.linesSvg.setAttribute('height', g.roomH);

    if (this.backRect) {
      this.backRect.setAttribute('x', g.wallLeft);
      this.backRect.setAttribute('y', g.wallTop);
      this.backRect.setAttribute('width', g.wallWidth);
      this.backRect.setAttribute('height', g.wallHeight);
    }

    this.setPath('tl', `M 0 0 L ${g.wallLeft} ${g.wallTop}`);
    this.setPath('tr', `M ${g.roomW} 0 L ${g.wallRight} ${g.wallTop}`);
    this.setPath('bl', `M 0 ${g.roomH} L ${g.wallLeft} ${g.wallBottom}`);
    this.setPath('br', `M ${g.roomW} ${g.roomH} L ${g.wallRight} ${g.wallBottom}`);

    this.setGradient('tl', 0, 0, g.wallLeft, g.wallTop);
    this.setGradient('tr', g.roomW, 0, g.wallRight, g.wallTop);
    this.setGradient('bl', 0, g.roomH, g.wallLeft, g.wallBottom);
    this.setGradient('br', g.roomW, g.roomH, g.wallRight, g.wallBottom);
  }

  updateSurfaces(g) {
    const leftX = g.wallLeft;
    const rightX = g.wallRight;
    const topY = g.wallTop;
    const bottomY = g.wallBottom;

    if (this.surfaces.left) {
      this.surfaces.left.style.left = '0px';
      this.surfaces.left.style.top = '0px';
      this.surfaces.left.style.width = `${g.roomW}px`;
      this.surfaces.left.style.height = `${g.roomH}px`;
      this.surfaces.left.style.transform = '';
      this.surfaces.left.style.clipPath = `polygon(0 0, ${leftX}px ${topY}px, ${leftX}px ${bottomY}px, 0 100%)`;
      this.surfaces.left.style.zIndex = '1';
    }

    if (this.surfaces.right) {
      this.surfaces.right.style.left = '0px';
      this.surfaces.right.style.top = '0px';
      this.surfaces.right.style.width = `${g.roomW}px`;
      this.surfaces.right.style.height = `${g.roomH}px`;
      this.surfaces.right.style.transform = '';
      this.surfaces.right.style.clipPath = `polygon(${rightX}px ${topY}px, 100% 0, 100% 100%, ${rightX}px ${bottomY}px)`;
      this.surfaces.right.style.zIndex = '1';
    }

    if (this.surfaces.top) {
      this.surfaces.top.style.left = '0px';
      this.surfaces.top.style.top = '0px';
      this.surfaces.top.style.width = `${g.roomW}px`;
      this.surfaces.top.style.height = `${g.roomH}px`;
      this.surfaces.top.style.transform = '';
      this.surfaces.top.style.clipPath = `polygon(0 0, 100% 0, ${rightX}px ${topY}px, ${leftX}px ${topY}px)`;
      this.surfaces.top.style.zIndex = '1';
    }

    if (this.surfaces.bottom) {
      this.surfaces.bottom.style.left = '0px';
      this.surfaces.bottom.style.top = '0px';
      this.surfaces.bottom.style.width = `${g.roomW}px`;
      this.surfaces.bottom.style.height = `${g.roomH}px`;
      this.surfaces.bottom.style.transform = '';
      this.surfaces.bottom.style.clipPath = `polygon(${leftX}px ${bottomY}px, ${rightX}px ${bottomY}px, 100% 100%, 0 100%)`;
      this.surfaces.bottom.style.zIndex = '1';
    }

    if (this.surfaces.middle) {
      this.surfaces.middle.style.left = `${g.wallLeft}px`;
      this.surfaces.middle.style.top = `${g.wallTop}px`;
      this.surfaces.middle.style.width = `${g.wallWidth}px`;
      this.surfaces.middle.style.height = `${g.wallHeight}px`;
      this.surfaces.middle.style.transform = '';
      this.surfaces.middle.style.clipPath = 'none';
      this.surfaces.middle.style.zIndex = '3';
    }
  }

  updatePlanes(g) {
    const leftTransform = `translateX(-100%) rotateY(${g.rotateAngle}deg)`;
    const rightTransform = `rotateY(-${g.rotateAngle}deg)`;

    this.positionBox(this.planes.left, g.wallLeft, g.wallTop, g.leftPlaneWidth, g.wallHeight, leftTransform);
    this.positionBox(this.planes.back, '50%', '50%', g.wallWidth, g.wallHeight, 'translate(-50%, -50%)');
    this.positionBox(this.planes.right, g.wallRight, g.wallTop, g.rightPlaneWidth, g.wallHeight, rightTransform);
  }

  updateLayout() {
    this.geometry = this.getGeometry();
    const mediaHeight = this.getMediaHeight(this.geometry);

    this.section.style.setProperty('--dgr-media-h', `${mediaHeight}px`);
    this.section.style.setProperty('--dgr-wall-bottom', `${this.geometry.wallBottom}px`);
    this.updateSurfaces(this.geometry);
    this.updatePlanes(this.geometry);
    this.updateLines(this.geometry);

    const firstSet = this.tracks.back.querySelector('[data-set]');

    if (firstSet) {
      const trackStyle = getComputedStyle(this.tracks.back);
      const gapValue = parseFloat(trackStyle.columnGap || trackStyle.gap || 0);
      this.setWidth = Math.max(1, firstSet.getBoundingClientRect().width + gapValue);
    }

    this.scroll = this.setWidth + (this.scroll % this.setWidth);
  }

  normalizeScroll() {
    if (!this.setWidth) return;
    this.scroll = ((this.scroll % this.setWidth) + this.setWidth) % this.setWidth;
  }

  getLoopedTrackX(offset = 0) {
    if (!this.setWidth) return 0;
    const looped = ((this.scroll + offset) % this.setWidth + this.setWidth) % this.setWidth;
    return -looped;
  }

  render() {
    if (!this.geometry) return;

    this.normalizeScroll();
    this.tracks.left.style.transform = `translate3d(${this.getLoopedTrackX(0)}px, -50%, 0)`;
    this.tracks.back.style.transform = `translate3d(${this.getLoopedTrackX(this.geometry.backStart)}px, -50%, 0)`;
    this.tracks.right.style.transform = `translate3d(${this.getLoopedTrackX(this.geometry.rightStart)}px, -50%, 0)`;
  }

  updatePointerOverMedia(event) {
    if (!this.pauseOnHover || this.pauseStrength <= 0) {
      this.pointerOverMedia = false;
      return;
    }

    this.pointerOverMedia = !!this.findLinkAtPoint(event.clientX, event.clientY);

    if (!this.pointerOverMedia) {
      const elements = document.elementsFromPoint(event.clientX, event.clientY);
      this.pointerOverMedia = elements.some((el) => this.section.contains(el) && el.closest && el.closest('.dgr-item'));
    }
  }

  tick(timestamp) {
    if (!this.lastFrame) this.lastFrame = timestamp;
    const dt = Math.min(0.05, (timestamp - this.lastFrame) / 1000);
    this.lastFrame = timestamp;
    let hoverMultiplier = 1;

    if (this.pauseOnHover && this.pointerOverMedia) {
      hoverMultiplier = 1 - this.pauseStrength;
    }

    if (this.autoplay && !this.dragging && hoverMultiplier > 0) {
      this.scroll += this.autoplaySpeed * 9 * dt * hoverMultiplier;
    }

    if (!this.dragging && Math.abs(this.velocity) > 1) {
      this.scroll += this.velocity * dt;
      this.velocity *= Math.pow(0.92, dt * 60);
    }

    this.render();
    this.raf = requestAnimationFrame(this.tick);
  }

  pointerDown(event) {
    this.dragging = true;
    this.moved = false;
    this.velocity = 0;
    this.lastX = event.clientX;
    this.lastTime = performance.now();
    this.pointerDownX = event.clientX;
    this.pointerDownY = event.clientY;
    this.pointerDownLink = event.target.closest && event.target.closest('.dgr-link') || this.findLinkAtPoint(event.clientX, event.clientY);
    this.room.classList.add('is-dragging');
    this.room.setPointerCapture && this.room.setPointerCapture(event.pointerId);
  }

  pointerMove(event) {
    this.updatePointerOverMedia(event);
    if (!this.dragging) return;

    const now = performance.now();
    const dt = Math.max(0.016, (now - this.lastTime) / 1000);
    const dx = event.clientX - this.lastX;
    const totalDx = event.clientX - this.pointerDownX;
    const totalDy = event.clientY - this.pointerDownY;

    if (Math.hypot(totalDx, totalDy) > 4) this.moved = true;

    this.scroll -= dx;
    this.velocity = -dx / dt;
    this.lastX = event.clientX;
    this.lastTime = now;
    this.render();
  }

  pointerUp(event) {
    if (!this.dragging) return;

    this.dragging = false;
    this.room.classList.remove('is-dragging');

    if (this.moved) {
      this.suppressClickUntil = Date.now() + 250;
    } else {
      const releaseLink =
        event.target.closest && event.target.closest('.dgr-link') ||
        this.findLinkAtPoint(event.clientX, event.clientY) ||
        this.pointerDownLink;

      if (releaseLink && releaseLink.href) {
        event.preventDefault && event.preventDefault();
        event.stopPropagation && event.stopPropagation();
        this.handleLinkSelect(releaseLink);
      }
    }

    this.pointerDownLink = null;
    this.room.releasePointerCapture && this.room.releasePointerCapture(event.pointerId);
  }

  handleLinkSelect(link) {
    const productUrl = link.dataset.dgrProductUrl || link.getAttribute('href') || '';
    const isProduct = /\/products\//.test(productUrl);

    if (isProduct) {
      if (!window.KRSProductLoader) {
        window.KRSProductLoader = new KRSInlineProductLoader();
      }

      window.KRSProductLoader.load(productUrl, {
        title: link.dataset.dgrProductTitle || link.getAttribute('aria-label') || 'product',
        sizeChartImage: link.dataset.dgrSizeChart || ''
      });
      return;
    }

    if (link.href) window.location.href = link.href;
  }

  syncThemeColor() {
    let color = '';
    if (this.surfaces.top) {
      color = getComputedStyle(this.surfaces.top).backgroundColor;
    }
    if (!color || color === 'rgba(0, 0, 0, 0)' || color === 'transparent') {
      color = getComputedStyle(this.section).backgroundColor;
    }
    if (!color) return;

    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'theme-color');
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', color);
  }

  scheduleRebuild() {
    clearTimeout(this.rebuildTimer);
    this.rebuildTimer = setTimeout(() => {
      this.applyLockedMobileHeight();
      this.updateLayout();
      this.render();
      this.syncThemeColor();
    }, 80);
  }

  bind() {
    this.section.addEventListener('dragstart', (event) => event.preventDefault());

    this.section.addEventListener('click', (event) => {
      const link = event.target.closest && event.target.closest('.dgr-link');

      if (Date.now() < this.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (!link || !this.section.contains(link)) return;

      const productUrl = link.dataset.dgrProductUrl || link.getAttribute('href') || '';

      if (/\/products\//.test(productUrl)) {
        event.preventDefault();
        event.stopPropagation();
        this.handleLinkSelect(link);
      }
    }, true);

    this.room.addEventListener('pointerdown', this.pointerDown);
    window.addEventListener('pointermove', this.pointerMove);
    window.addEventListener('pointerup', this.pointerUp);
    window.addEventListener('pointercancel', this.pointerUp);
    this.room.addEventListener('pointerleave', () => { this.pointerOverMedia = false; });
    window.addEventListener('resize', this.scheduleRebuild);

    this.section.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.scheduleRebuild);
    });

    this.section.querySelectorAll('video').forEach((video) => {
      video.muted = true;
      video.loop = true;
      video.autoplay = true;
      video.playsInline = true;
      video.addEventListener('loadedmetadata', this.scheduleRebuild);
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
    });
  }

  destroy() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.rebuildTimer);
    window.removeEventListener('pointermove', this.pointerMove);
    window.removeEventListener('pointerup', this.pointerUp);
    window.removeEventListener('pointercancel', this.pointerUp);
    window.removeEventListener('resize', this.scheduleRebuild);
  }
}

function initDBRMANGalleryRooms(root = document) {
  root.querySelectorAll('[data-dgr-section]').forEach((section) => {
    if (section.__dbrmanGalleryRoom) return;
    section.__dbrmanGalleryRoom = new DBRMANGalleryRoom(section);
  });
}

document.addEventListener('DOMContentLoaded', () => initDBRMANGalleryRooms());
document.addEventListener('shopify:section:load', (event) => initDBRMANGalleryRooms(event.target));
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-dgr-section]').forEach((section) => {
    if (section.__dbrmanGalleryRoom) {
      section.__dbrmanGalleryRoom.destroy();
      section.__dbrmanGalleryRoom = null;
    }
  });
});

class KRSThemeCart {
  constructor(page) {
    this.page = page;
    this.drawer = page.querySelector('[data-dgr-cart-drawer]');
    this.overlay = page.querySelector('[data-dgr-cart-overlay]');
    this.items = page.querySelector('[data-dgr-cart-items]');
    this.countEls = page.querySelectorAll('[data-dgr-cart-count]');
    this.subtotalEl = page.querySelector('[data-dgr-cart-subtotal]');
    this.shippingValue = page.querySelector('[data-dgr-shipping-value]');
    this.flatShippingCents = 1000;
    this.isUSCustomer = this.detectUSCustomer();
    this.emptyText = this.drawer && this.drawer.dataset.emptyText ? this.drawer.dataset.emptyText : 'YOUR CART IS EMPTY';

    if (!this.drawer || !this.items) return;

    this.bind();
    this.refresh(false);
  }

  bind() {
    this.page.querySelectorAll('[data-dgr-open-cart]').forEach((btn) => {
      btn.addEventListener('click', () => this.open());
    });

    this.page.querySelectorAll('[data-dgr-close-cart]').forEach((btn) => {
      btn.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.page.classList.contains('is-cart-open')) {
        this.close();
      }
    });

    document.addEventListener('cart:refresh', () => this.refresh(false));
    document.addEventListener('cart:update', () => this.refresh(false));
  }

  open() {
    this.page.classList.add('is-cart-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('dgr-cart-lock');
    if (document.body) document.body.classList.add('dgr-cart-lock');
    this.refresh(false);
  }

  close() {
    this.page.classList.remove('is-cart-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('dgr-cart-lock');
    if (document.body) document.body.classList.remove('dgr-cart-lock');
  }

  async refresh(openAfter = false) {
    try {
      const res = await fetch('/cart.js', {
        headers: { Accept: 'application/json' }
      });

      const cart = await res.json();
      this.render(cart);

      if (openAfter) this.open();
    } catch (error) {
      console.warn('KRS cart refresh failed', error);
    }
  }

  detectUSCustomer() {
    const locale = (navigator.language || navigator.userLanguage || '').toLowerCase();
    const languages = Array.isArray(navigator.languages) ? navigator.languages.join(',').toLowerCase() : '';
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';

    const usTimeZones = [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Phoenix',
      'America/Anchorage',
      'Pacific/Honolulu'
    ];

    return locale.includes('us') || languages.includes('en-us') || usTimeZones.includes(timeZone);
  }

  money(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, (c) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[c]));
  }

  updateLine(key, quantity) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ id: key, quantity })
    })
      .then((res) => res.json())
      .then((cart) => this.render(cart));
  }

  render(cart) {
    const count = cart && cart.item_count ? cart.item_count : 0;

    this.countEls.forEach((el) => {
      el.textContent = count;
    });

    const domesticShippingCents = this.isUSCustomer && cart && cart.items && cart.items.length ? this.flatShippingCents : 0;
    const drawerTotalCents = (cart && cart.total_price ? cart.total_price : 0) + domesticShippingCents;

    if (this.subtotalEl) {
      this.subtotalEl.textContent = this.money(drawerTotalCents);
    }

    const shippingLine = this.page.querySelector('[data-dgr-shipping-line]');
    const subtotalLine = this.subtotalEl ? this.subtotalEl.closest('.dgr-cart-line') : null;
    const checkoutButton = this.page.querySelector('.dgr-cart-checkout');

    if (!cart || !cart.items || !cart.items.length) {
      this.items.innerHTML = `<div class="dgr-cart-empty">${this.escapeHtml(this.emptyText)}</div>`;

      if (shippingLine) shippingLine.style.display = 'none';
      if (subtotalLine) subtotalLine.style.display = 'none';

      if (checkoutButton) {
        checkoutButton.style.display = 'flex';
        checkoutButton.textContent = 'BACK TO SHOP';
        checkoutButton.classList.add('dgr-cart-empty-back');
        checkoutButton.onclick = (event) => {
          event.preventDefault();
          this.close();
        };
      }

      return;
    }

    if (shippingLine && this.shippingValue) {
      if (this.isUSCustomer) {
        shippingLine.hidden = false;
        shippingLine.style.display = '';
        this.shippingValue.textContent = this.money(this.flatShippingCents);
      } else {
        shippingLine.hidden = true;
        shippingLine.style.display = 'none';
      }
    }

    if (subtotalLine) subtotalLine.style.display = '';

    if (checkoutButton) {
      checkoutButton.style.display = 'flex';
      checkoutButton.textContent = 'CHECKOUT NOW →';
      checkoutButton.classList.remove('dgr-cart-empty-back');
      checkoutButton.onclick = (event) => {
        event.preventDefault();
        window.location.href = '/checkout';
      };
    }

    this.items.innerHTML = cart.items.map((item) => {
      const image = item.image
        ? `<a class="dgr-cart-item__image" href="${this.escapeHtml(item.url || '#')}"><img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title || '')}" loading="lazy"></a>`
        : '<span class="dgr-cart-item__image"></span>';

      const options = (item.options_with_values || [])
        .filter((o) => o.value && o.value !== 'Default Title')
        .map((o) => `${this.escapeHtml(o.name)}: ${this.escapeHtml(o.value)}`)
        .join('<br>');

      return `
        <div class="dgr-cart-item" data-key="${this.escapeHtml(item.key)}">
          ${image}
          <div class="dgr-cart-item__body">
            <a class="dgr-cart-item__title" href="${this.escapeHtml(item.url || '#')}">${this.escapeHtml(item.product_title || item.title || '')}</a>
            ${options ? `<div class="dgr-cart-item__meta">${options}</div>` : ''}
            <div class="dgr-cart-item__qty">
              <button type="button" class="dgr-cart-qty-btn" data-dgr-qty-minus aria-label="Decrease quantity">−</button>
              <span>${item.quantity}</span>
              <button type="button" class="dgr-cart-qty-btn" data-dgr-qty-plus aria-label="Increase quantity">+</button>
              <button type="button" class="dgr-cart-remove" data-dgr-remove>REMOVE</button>
            </div>
          </div>
          <div class="dgr-cart-item__price">${this.money(item.final_line_price)}</div>
        </div>`;
    }).join('');

    this.items.querySelectorAll('.dgr-cart-item').forEach((row, index) => {
      const item = cart.items[index];

      const minus = row.querySelector('[data-dgr-qty-minus]');
      const plus = row.querySelector('[data-dgr-qty-plus]');
      const remove = row.querySelector('[data-dgr-remove]');

      if (minus) minus.addEventListener('click', () => this.updateLine(item.key, Math.max(0, item.quantity - 1)));
      if (plus) plus.addEventListener('click', () => this.updateLine(item.key, item.quantity + 1));
      if (remove) remove.addEventListener('click', () => this.updateLine(item.key, 0));
    });
  }
}

function initKRSCarts(root = document) {
  root.querySelectorAll('.dgr-page').forEach((page) => {
    if (page.__krsThemeCart) return;
    page.__krsThemeCart = new KRSThemeCart(page);
  });
}

function initKRSTypeout(root = document) {
  root.querySelectorAll('[data-dgr-typeout]').forEach((el) => {
    if (el.__dgrTypeoutStarted) return;

    el.__dgrTypeoutStarted = true;
    const text = el.dataset.text || '';
    const hold = Math.max(0, Number(el.dataset.hold || 2000));
    let i = 0;
    const typeSpeed = 42;
    const backSpeed = 24;

    const type = () => {
      el.textContent = text.slice(0, i++);
      if (i <= text.length) setTimeout(type, typeSpeed);
      else setTimeout(backspace, hold);
    };

    const backspace = () => {
      i -= 1;
      el.textContent = text.slice(0, Math.max(0, i));
      if (i > 0) setTimeout(backspace, backSpeed);
      else el.classList.add('is-done');
    };

    type();
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initKRSCarts();
  initKRSTypeout();
});

document.addEventListener('shopify:section:load', (event) => {
  initKRSCarts(event.target);
  initKRSTypeout(event.target);
});

class KRSInlineProductLoader {
  constructor() {
    this.section = document.querySelector('[data-dgr-selected-product]');
    this.content = document.querySelector('[data-dgr-product-content]');
    this.loading = document.querySelector('[data-dgr-product-loading]');
    this.currentUrl = '';
    this.cache = new Map();
    this.preloadStarted = false;

    this.preloadProducts();
  }

  normalizeUrl(url) {
    return new URL(url, window.location.origin).pathname;
  }

  escapeHtml(value = '') {
    return String(value).replace(/[&<>'"]/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      "'": '&#39;',
      '"': '&quot;'
    }[char]));
  }

  money(cents) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify && window.Shopify.currency ? window.Shopify.currency.active : 'USD'
      }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return `$${((Number(cents) || 0) / 100).toFixed(2)}`;
    }
  }

  async fetchProductSection(url) {
    const cleanUrl = this.normalizeUrl(url);

    if (this.cache.has(cleanUrl)) {
      return this.cache.get(cleanUrl);
    }

    const productJsonUrl = `${cleanUrl}.js`;

    const promise = Promise.all([
      fetch(productJsonUrl, {
        headers: { Accept: 'application/json' }
      }).then((res) => {
        if (!res.ok) throw new Error(`Product JSON failed: ${res.status}`);
        return res.json();
      }),
      fetch(cleanUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      }).then((res) => res.ok ? res.text() : '')
    ]).then(([product, pageHtml]) => {
      this.preloadProductImages(product);
      return this.renderProductFromJson(product, this.extractProductExtras(pageHtml));
    });

    this.cache.set(cleanUrl, promise);
    return promise;
  }

  extractProductExtras(pageHtml = '') {
  if (!pageHtml) return '';

  try {
    const doc = new DOMParser().parseFromString(pageHtml, 'text/html');

    const detailsRoot =
      doc.querySelector('[id^="ProductInfo-"]') ||
      doc.querySelector('.product-information') ||
      doc.querySelector('.product__info-container') ||
      doc.querySelector('.product-details') ||
      doc.querySelector('[data-product-details]') ||
      doc;

    const nodes = [];

    const selectors = [
      '.product__accordion',
      '[data-block-type="collapsible_row"]',
      '[data-block-type="accordion"]',
      'accordion-custom',
      'details-disclosure'
    ];

    selectors.forEach((selector) => {
      detailsRoot.querySelectorAll(selector).forEach((node) => {
        const text = (node.textContent || '').trim();

        if (!text) return;

        const lowerText = text.toLowerCase();

        if (
          lowerText.includes('size') &&
          lowerText.includes('add to cart')
        ) {
          return;
        }

        if (nodes.some((existing) => existing.contains(node) || node.contains(existing))) return;

        nodes.push(node.cloneNode(true));
      });
    });

    if (!nodes.length) {
      detailsRoot.querySelectorAll('details').forEach((node) => {
        const text = (node.textContent || '').trim();

        if (!text) return;
        if (nodes.some((existing) => existing.contains(node) || node.contains(existing))) return;

        nodes.push(node.cloneNode(true));
      });
    }

    if (!nodes.length) return '';

    return nodes.map((node) => node.outerHTML).join('');
  } catch (error) {
    console.warn('KRS product extras failed:', error);
    return '';
  }
}

  preloadProductImages(product) {
    const images = product && product.images ? product.images : [];

    images.forEach((image) => {
      const src = typeof image === 'string' ? image : image.src;
      if (!src) return;

      const img = new Image();
      img.src = src;
    });
  }

  renderProductFromJson(product, extraHtml = '') {
    const variants = product.variants || [];
    const firstAvailableVariant = variants.find((variant) => variant.available) || variants[0];
    const images = product.images || [];

    const imageHtml = images.length
      ? `
        <div class="dgr-inline-product__media" data-dgr-inline-media>
          <div class="dgr-inline-product__main-track" data-dgr-inline-main-track>
            ${images.map((image, index) => {
              const src = typeof image === 'string' ? image : image.src;
              return `<div class="dgr-inline-product__main-slide" data-dgr-inline-slide="${index}"><img src="${this.escapeHtml(src)}" alt="${this.escapeHtml(product.title)}" loading="eager"></div>`;
            }).join('')}
          </div>

          ${images.length > 1 ? `
            <div class="dgr-inline-product__thumbs" data-dgr-inline-thumbs>
              ${images.map((image, index) => {
                const src = typeof image === 'string' ? image : image.src;
                return `<button type="button" class="dgr-inline-product__thumb${index === 0 ? ' is-active' : ''}" data-dgr-inline-thumb="${index}" aria-label="Show product image ${index + 1}"><img src="${this.escapeHtml(src)}" alt="" loading="lazy"></button>`;
              }).join('')}
            </div>

            <div class="dgr-inline-product__dots" data-dgr-inline-dots>
              ${images.map((_, index) => `<button type="button" class="dgr-inline-product__dot${index === 0 ? ' is-active' : ''}" data-dgr-inline-dot="${index}" aria-label="Show product image ${index + 1}"></button>`).join('')}
            </div>` : ''}
        </div>`
      : '';

    const hasRealVariants = variants.length > 1 || (variants[0] && variants[0].title && variants[0].title !== 'Default Title');

    const variantSelectHtml = hasRealVariants
  ? `
    <div class="dgr-inline-product__variant-label">
      <span>SIZE</span>

      <div class="dgr-inline-product__variant-boxes" data-dgr-inline-variant-boxes>
        ${variants.map((variant) => {
          const isSelected = firstAvailableVariant && String(firstAvailableVariant.id) === String(variant.id);

          return `
            <label class="dgr-inline-product__variant-box${isSelected ? ' is-selected' : ''}${variant.available ? '' : ' is-sold-out'}">
              <input
                type="radio"
                name="id"
                value="${this.escapeHtml(variant.id)}"
                data-price="${this.escapeHtml(variant.price)}"
                data-available="${variant.available ? 'true' : 'false'}"
                ${isSelected ? 'checked' : ''}
                ${variant.available ? '' : 'disabled'}
              >
              <span>${this.escapeHtml(variant.title)}</span>
            </label>
          `;
        }).join('')}
      </div>
    </div>`
  : `<input type="hidden" name="id" value="${this.escapeHtml(firstAvailableVariant ? firstAvailableVariant.id : '')}">`;

    const available = !!(firstAvailableVariant && firstAvailableVariant.available);

    return `
      <div class="dgr-inline-product" data-dgr-inline-product>
        <div class="dgr-inline-product__left">
          ${imageHtml}
        </div>

        <div class="dgr-inline-product__right">
          <h1 class="dgr-inline-product__title">${this.escapeHtml(product.title)}</h1>

          <div class="dgr-inline-product__price" data-dgr-inline-price>
            ${this.money(firstAvailableVariant ? firstAvailableVariant.price : product.price || 0)}
          </div>

          <form class="dgr-inline-product__form" data-dgr-inline-product-form>
            ${variantSelectHtml}

            <button
              type="submit"
              class="dgr-inline-product__button"
              data-dgr-inline-product-submit
              ${available ? '' : 'disabled'}
            >
              ${available ? 'Add to cart' : 'Sold out'}
            </button>
          </form>

          ${product.description ? `
            <div class="dgr-inline-product__description">
              ${product.description}
            </div>` : ''}

          ${extraHtml ? `
            <div class="dgr-inline-product__extras">
              ${extraHtml}
            </div>` : ''}
        </div>
      </div>`;
  }

  preloadProducts() {
    if (this.preloadStarted) return;
    this.preloadStarted = true;

    const preload = () => {
      document.querySelectorAll('.dgr-link[data-dgr-product-url], .dgr-link[href*="/products/"]').forEach((link) => {
        const url = link.dataset.dgrProductUrl || link.getAttribute('href');
        if (url && /\/products\//.test(url)) {
          this.fetchProductSection(url).catch(() => {});
        }
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(preload);
    } else {
      setTimeout(preload, 400);
    }
  }

  async loadPage(url, meta = {}) {
    if (!this.section || !this.content || !url) {
      window.location.href = url;
      return;
    }

    const cleanUrl = this.normalizeUrl(url);
    this.currentUrl = cleanUrl;

    this.section.classList.remove('is-loaded');
    this.section.classList.add('is-loading');

    if (this.loading) this.loading.hidden = false;

    try {
      const res = await fetch(cleanUrl, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });

      const html = await res.text();
      if (this.currentUrl !== cleanUrl) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const mainContent =
        doc.querySelector('#MainContent') ||
        doc.querySelector('main') ||
        doc.querySelector('.content-for-layout') ||
        doc.body;

      if (!mainContent) throw new Error('Could not find page content.');

      this.content.innerHTML = `
        <div class="dgr-inline-page">
          ${mainContent.innerHTML}
        </div>`;

      this.section.classList.remove('is-loading');
      this.section.classList.add('is-loaded');

      if (this.loading) this.loading.hidden = true;

      this.afterInject();
      this.scrollToProductArea();
      history.replaceState(null, '', `#${this.slugify(meta.title || 'page')}`);
    } catch (error) {
      console.warn('KRS inline page load failed:', error);
      window.location.href = cleanUrl;
    }
  }

  async load(url, meta = {}) {
    if (!this.section || !this.content || !url) {
      window.location.href = url;
      return;
    }

    const cleanUrl = this.normalizeUrl(url);
    this.currentUrl = cleanUrl;
    this.activeSizeChartImage = meta.sizeChartImage || '';
    const cached = this.cache.has(cleanUrl);

    this.section.classList.remove('is-loaded');

    if (!cached) {
      this.section.classList.add('is-loading');
      if (this.loading) this.loading.hidden = false;
    }

    try {
      const html = await this.fetchProductSection(cleanUrl);
      if (this.currentUrl !== cleanUrl) return;

      this.content.innerHTML = html;
      this.section.classList.remove('is-loading');
      this.section.classList.add('is-loaded');

      if (this.loading) this.loading.hidden = true;

      this.afterInject();
      this.scrollToProductArea();
      history.replaceState(null, '', `#${this.slugify(meta.title || 'product')}`);
    } catch (error) {
      console.warn('KRS inline product load failed:', error);

      this.content.innerHTML = `
        <div class="dgr-inline-page">
          <p>PRODUCT COULD NOT LOAD INLINE.</p>
        </div>`;

      this.section.classList.remove('is-loading');
      this.section.classList.add('is-loaded');

      if (this.loading) this.loading.hidden = true;

      this.scrollToProductArea();
    }
  }

  scrollToProductArea() {
    requestAnimationFrame(() => {
      const top = this.section.getBoundingClientRect().top + window.pageYOffset;

      window.scrollTo({
        top,
        behavior: 'smooth'
      });
    });
  }

  slugify(value) {
    return String(value || 'product')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'product';
  }

  injectSizeChartOverride(root) {
    const sizeChartImage = this.activeSizeChartImage;

    const right = root.querySelector('.dgr-inline-product__right');
    if (!right) return;

    const extras = right.querySelector('.dgr-inline-product__extras');

    // Always remove any size-chart accordion scraped from the product page
    if (extras) {
      extras
        .querySelectorAll('details, .product__accordion, accordion-custom, details-disclosure, collapsible-row, .accordion')
        .forEach((node) => {
          const head = node.querySelector('summary, .accordion__title, .accordion__summary, .details__header, .summary__title');
          const label = ((head ? head.textContent : node.textContent) || '').toLowerCase();
          if (label.includes('size')) node.remove();
        });

      // If stripping the chart left the extras container empty, remove it too
      // so we don't leave an empty bordered block behind
      if (!extras.querySelector('details, .product__accordion, accordion-custom, details-disclosure, collapsible-row, .accordion')) {
        extras.remove();
      }
    }

    // Nothing uploaded for this item → show no size chart at all
    if (!sizeChartImage) return;

    // Otherwise add our own size chart, recreating the container if needed
    let container = right.querySelector('.dgr-inline-product__extras');
    if (!container) {
      container = document.createElement('div');
      container.className = 'dgr-inline-product__extras';
      right.appendChild(container);
    }

    const details = document.createElement('details');
    details.className = 'dgr-size-chart-accordion';
    details.innerHTML =
      '<summary>Size chart</summary>' +
      '<div class="dgr-size-chart-accordion__body">' +
      '<img src="' + this.escapeHtml(sizeChartImage) + '" alt="Size chart" loading="lazy">' +
      '</div>';
    container.appendChild(details);
  }

  afterInject() {
    const root = this.content;

    this.injectSizeChartOverride(root);

    root.querySelectorAll('script').forEach((oldScript) => {
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });

    document.dispatchEvent(new CustomEvent('shopify:section:load', {
      detail: { source: 'krs-inline-product' },
      bubbles: true
    }));

    document.dispatchEvent(new CustomEvent('krs:inline-product:loaded', {
      detail: { root }
    }));

    root.querySelectorAll('[data-dgr-inline-media]').forEach((media) => {
      if (media.__dgrInlineMediaBound) return;
      media.__dgrInlineMediaBound = true;

      const track = media.querySelector('[data-dgr-inline-main-track]');
      const thumbs = media.querySelectorAll('[data-dgr-inline-thumb]');
      const dots = media.querySelectorAll('[data-dgr-inline-dot]');
      if (!track) return;

      const openedAt = performance.now();
      const lockToFirst = () => {
        const prevSnap = track.style.scrollSnapType;
        track.style.scrollSnapType = 'none';
        track.scrollLeft = 0;
        requestAnimationFrame(() => { track.style.scrollSnapType = prevSnap; });
      };

      lockToFirst();
      track.querySelectorAll('img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', () => {
          if (performance.now() - openedAt < 1500) lockToFirst();
        }, { once: true });
      });

      const setActive = (index) => {
        thumbs.forEach((thumb) => thumb.classList.toggle('is-active', Number(thumb.dataset.dgrInlineThumb) === index));
        dots.forEach((dot) => dot.classList.toggle('is-active', Number(dot.dataset.dgrInlineDot) === index));
      };

      const goTo = (index) => {
        const slide = track.querySelector(`[data-dgr-inline-slide="${index}"]`);
        if (slide) slide.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        setActive(index);
      };

      thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.dgrInlineThumb || 0))));
      dots.forEach((dot) => dot.addEventListener('click', () => goTo(Number(dot.dataset.dgrInlineDot || 0))));

      track.querySelectorAll('.dgr-inline-product__main-slide img').forEach((img) => {
  img.addEventListener('click', () => {
    if (window.matchMedia('(max-width: 749px)').matches) return;

    const lightbox = document.createElement('div');
    lightbox.className = 'dgr-product-lightbox';
    lightbox.innerHTML = `
      <button type="button" class="dgr-product-lightbox__close" aria-label="Close">×</button>
      <img src="${img.currentSrc || img.src}" alt="">
    `;

    document.body.appendChild(lightbox);

    const close = () => lightbox.remove();

    lightbox.addEventListener('click', (event) => {
      if (
        event.target === lightbox ||
        event.target.closest('.dgr-product-lightbox__close')
      ) {
        close();
      }
    });

    document.addEventListener('keydown', function esc(event) {
      if (event.key === 'Escape') {
        close();
        document.removeEventListener('keydown', esc);
      }
    });
  });
});

      let raf = null;
      track.addEventListener('scroll', () => {
        if (raf) cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          setActive(index);
        });
      }, { passive: true });
    });

    root.querySelectorAll('[data-dgr-inline-product-form]').forEach((form) => {
      if (form.__dgrInlineProductBound) return;
      form.__dgrInlineProductBound = true;

      const variantInputs = form.querySelectorAll('input[name="id"]');
      const price = root.querySelector('[data-dgr-inline-price]');
      const submit = form.querySelector('[data-dgr-inline-product-submit]');

      variantInputs.forEach((input) => {
        input.addEventListener('change', () => {
          const optionPrice = input.dataset ? input.dataset.price : '';
          const available = input.dataset ? input.dataset.available === 'true' : true;

          form.querySelectorAll('.dgr-inline-product__variant-box').forEach((box) => {
            box.classList.toggle('is-selected', box.contains(input));
          });

          if (price && optionPrice) price.textContent = this.money(optionPrice);

          if (submit) {
            submit.disabled = !available;
            submit.textContent = available ? 'Add to cart' : 'Sold out';
          }
        });
      });

      form.addEventListener('submit', async (event) => {
        event.preventDefault();

        const formData = new FormData(form);
        const id = formData.get('id');

        if (!id) return;

        if (submit) {
          submit.disabled = true;
          submit.textContent = 'Adding...';
        }

        try {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: new URLSearchParams({ id, quantity: '1' })
          });

          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:update'));

          const page = document.querySelector('.dgr-page');
          if (page && page.__krsThemeCart) {
            page.__krsThemeCart.refresh(true);
          }

          if (submit) {
            submit.disabled = false;
            submit.textContent = 'Added';

            setTimeout(() => {
              const currentInput = form.querySelector('input[name="id"]:checked');
              const available = currentInput ? currentInput.dataset.available === 'true' : true;
              submit.textContent = available ? 'Add to cart' : 'Sold out';
              submit.disabled = !available;
            }, 900);
          }
        } catch (error) {
          console.warn('KRS inline add to cart failed:', error);

          if (submit) {
            submit.disabled = false;
            submit.textContent = 'Add to cart';
          }
        }
      });
    });
  }
}

function initKRSInlineProductLoader() {
  if (!window.KRSProductLoader) {
    window.KRSProductLoader = new KRSInlineProductLoader();
  }
}

function initKRSInlineProductLinks() {
  if (window.__krsInlineProductLinksBound) return;
  window.__krsInlineProductLinksBound = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('.dgr-link');
    if (!link) return;

    const productUrl = link.dataset.dgrProductUrl || link.getAttribute('href') || '';
    if (!productUrl || !/\/products\//.test(productUrl)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!window.KRSProductLoader) {
      window.KRSProductLoader = new KRSInlineProductLoader();
    }

    window.KRSProductLoader.load(productUrl, {
      title: link.dataset.dgrProductTitle || link.getAttribute('aria-label') || 'product',
      sizeChartImage: link.dataset.dgrSizeChart || ''
    });
  }, true);
}

function initKRSInlineFooterLinks() {
  if (window.__krsInlineFooterLinksBound) return;
  window.__krsInlineFooterLinksBound = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest && event.target.closest('.krs-footer a');
    if (!link) return;

    const href = link.getAttribute('href') || '';

    if (!href) return;
    if (href.startsWith('#')) return;
    if (href.startsWith('mailto:')) return;
    if (href.startsWith('tel:')) return;
    if (href.includes('/products/')) return;

    const isExternal = href.startsWith('http') && !href.includes(window.location.hostname);
    if (isExternal) return;

    event.preventDefault();
    event.stopPropagation();

    if (!window.KRSProductLoader) {
      window.KRSProductLoader = new KRSInlineProductLoader();
    }

    window.KRSProductLoader.loadPage(href, {
      title: link.textContent || 'page'
    });
  });
}

function forceGalleryStartAtTop() {
  const hasGallery = document.querySelector('[data-dgr-section]');
  if (!hasGallery) return;

  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  const hash = window.location.hash || '';
  const shouldStayAtHash = hash && hash !== '#';

  if (shouldStayAtHash) return;

  window.scrollTo(0, 0);

  requestAnimationFrame(() => {
    window.scrollTo(0, 0);
  });

  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 80);

  setTimeout(() => {
    window.scrollTo(0, 0);
  }, 250);
}

function initKRSAll(root = document) {
  forceGalleryStartAtTop();
  initDBRMANGalleryRooms(root);
  initKRSCarts(root);
  initKRSTypeout(root);
  initKRSInlineProductLoader();
  initKRSInlineProductLinks();
  initKRSInlineFooterLinks();
}

window.addEventListener('pageshow', () => {
  forceGalleryStartAtTop();
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initKRSAll());
} else {
  initKRSAll();
}